import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const DEFAULT_FEATURES = [
    { name: "HR-SAFEZONE", enabled: false, settings: { min: 40, max: 120 } },
    { name: "INACTIVITY", enabled: false, settings: { threshold: 0.01 } },
    { name: "FALL-DETECTION", enabled: false, settings: { min: 40, max: 120 } },
    { name: "HR", enabled: false, settings: {} },
    { name: "STEPS", enabled: false, settings: {} }
];

const HealthProfileForm = ({ initialData = {}, onSave, onCancel, isEditMode = false }) => {
    const [t] = useTranslation(["global", "health"]);

    const [formData, setFormData] = useState({
        guid: initialData.guid || undefined,
        name: initialData.name || '',
        features: initialData.features || DEFAULT_FEATURES,
        emergencyNumber: initialData.emergencyNumber || ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFeatureToggle = (featureName) => {
        setFormData(prev => {
            const updated = prev.features.map(f =>
                f.name === featureName ? { ...f, enabled: !f.enabled } : f
            );
            return { ...prev, features: updated };
        });
    };

    const handleThresholdChange = (featureName, key, value) => {
        setFormData(prev => {
            const updated = prev.features.map(f =>
                f.name === featureName
                    ? { ...f, settings: { ...f.settings, [key]: Number(value) } }
                    : f
            );
            return { ...prev, features: updated };
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const getFeatureLabel = (featureName) => {
        switch (featureName) {
            case "HR-SAFEZONE": return <><i className="fa-solid fa-heart-pulse opacity-25"></i> {t("health:entity.profile.features.HR-SAFEZONE")}</>
            case "FALL-DETECTION": return <><i className="fa-solid fa-person-falling opacity-25"></i> {t("health:entity.profile.features.FALL-DETECTION")}</>
            case "INACTIVITY": return <><i className="fa-solid fa-notes-medical opacity-25"></i> {t("health:entity.profile.features.INACTIVITY")}</>
            case "HR": return <><i className="fa-solid fa-chart-line opacity-25"></i> {t("health:entity.profile.features.HR")}</>
            case "STEPS": return <><i className="fa-solid fa-shoe-prints opacity-25"></i> {t("health:entity.profile.features.STEPS")}</>
            case "TEMPS": return <><i className="fa-solid fa-temperature-three-quarters opacity-25"></i> {t("health:entity.profile.features.TEMPS")}</>
            case "VO2MAX": return <><i className="fa-solid fa-lungs opacity-25"></i> {t("health:entity.profile.features.VO2MAX")}</>
            default: return featureName
        }
    }

    return (
        <form onSubmit={handleSubmit} className="card bg-dark text-white p-3">
            <div className="card-body">
                <div className="mb-3">
                    <label className="form-label">{t("property.name")}</label>
                    <input
                        type="text"
                        name="name"
                        className="form-control"
                        value={formData.name}
                        onChange={handleChange}
                        disabled={initialData.isDefault}
                        required
                    />
                </div>

                <div className="mb-3">
                    <label className="form-label text-eh">{t("health:entity.profile.props.features")}</label>
                    {formData.features.map(feature => (
                        <div key={feature.name} className="form-check mb-2">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id={feature.name}
                                checked={feature.enabled}
                                onChange={() => handleFeatureToggle(feature.name)}
                            />
                            <label className="form-check-label" htmlFor={feature.name}>{getFeatureLabel(feature.name)}</label>

                            {feature.name === "HR-SAFEZONE" && feature.enabled && (
                                <div className="row mt-2 mb-5">
                                    <div className="col">
                                        <label className="form-label">MIN <small className="text-muted">bpm</small></label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={feature.settings.min}
                                            onChange={(e) => handleThresholdChange(feature.name, "min", e.target.value)}
                                        />
                                    </div>
                                    <div className="col">
                                        <label className="form-label">MAX <small className="text-muted">bpm</small></label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={feature.settings.max}
                                            onChange={(e) => handleThresholdChange(feature.name, "max", e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}

                            {feature.name === "INACTIVITY" && feature.enabled && (
                                <div className="row mt-2 mb-5">
                                    <div className="col">
                                        <label className="form-label">Threshold <small className="text-muted">m/s^2</small></label>
                                        <h4>{feature.settings.threshold}</h4>
                                        <input
                                            type="range"
                                            min="0.01"
                                            max="0.1"
                                            step="0.01"

                                            value={feature.settings.threshold}
                                            onChange={(e) => handleThresholdChange(feature.name, "threshold", e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}

                            {feature.name === "FALL-DETECTION" && feature.enabled && (
                                <div className="row mt-2 mb-5">
                                    <div className="col-6">
                                        <label className="form-label">Threshold <small className="text-muted">m/s^2</small></label>
                                        <h4>{feature.settings.threshold}</h4>
                                        <input
                                            type="range"
                                            min="10"
                                            max="40"
                                            step="1"
                                            value={feature.settings.threshold}
                                            onChange={(e) => handleThresholdChange(feature.name, "threshold", e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="mb-3">
                    <label className="form-label">{t("health:entity.profile.props.emergency_phone")}</label>
                    <input
                        type="text"
                        name="emergencyNumber"
                        className="form-control"
                        value={formData.emergencyNumber}
                        onChange={handleChange}
                    />
                </div>

                <div className="text-end">
                    <button type="submit" className="btn btn-primary mx-1">{t("action.save")}</button>
                    {onCancel && (
                        <button type="button" className="btn btn-secondary" onClick={onCancel}>{t("action.cancel")}</button>
                    )}
                </div>
            </div>
        </form>
    );
};

export default HealthProfileForm;
